import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { COMPANY, PRODUCTS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Enquiry() {
  const [searchParams] = useSearchParams();
  const rawParam = searchParams.get("product") || "";
  // Match by exact name OR by slug (case-insensitive) so product-detail page links work either way
  const matched = PRODUCTS.find(
    (p) => p.name === rawParam || p.slug.toLowerCase() === rawParam.toLowerCase()
  );
  const preselect = matched ? matched.name : rawParam;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product_interest: preselect,
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  useEffect(() => {
    if (preselect) setForm((f) => ({ ...f, product_interest: preselect }));
  }, [preselect]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      const res = await axios.post(`${API}/enquiry`, form, { timeout: 20000 });
      const sent = res.data?.email_sent;
      setStatus({
        state: "success",
        message: sent
          ? "Thank you! Your enquiry has been emailed to our team. We'll respond shortly."
          : "Thank you! Your enquiry has been received. We'll respond shortly.",
      });
      setForm({ name: "", email: "", phone: "", company: "", product_interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        message: typeof detail === "string"
          ? detail
          : "Could not submit enquiry. Please call or WhatsApp us instead.",
      });
    }
  };

  return (
    <div data-testid="enquiry-page">
      <section className="bg-[#0A192F] text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="slash-divider"></span>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9]">Enquiry Form</span>
          </div>
          <h1 className="font-head font-black text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
            Tell us what you need.
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl text-lg">
            Share the part, drawing or crusher model. We&rsquo;ll come back with stock, specs and
            best price.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <form onSubmit={submit} className="bg-white border border-[#E2E8F0] p-6 md:p-10" data-testid="enquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full Name" required>
                <input required value={form.name} onChange={update("name")} type="text" placeholder="Your name" className="input" data-testid="field-name" />
              </Field>
              <Field label="Phone" required>
                <input required value={form.phone} onChange={update("phone")} type="tel" placeholder="+91" className="input" data-testid="field-phone" />
              </Field>
              <Field label="Email" required>
                <input required value={form.email} onChange={update("email")} type="email" placeholder="you@company.com" className="input" data-testid="field-email" />
              </Field>
              <Field label="Company">
                <input value={form.company} onChange={update("company")} type="text" placeholder="Optional" className="input" data-testid="field-company" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Product Interest">
                  <select value={form.product_interest} onChange={update("product_interest")} className="input" data-testid="field-product">
                    <option value="">— Select a product (optional) —</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.slug} value={p.name}>{p.name}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Message / Specifications" required>
                  <textarea required value={form.message} onChange={update("message")} rows={6} placeholder="Part number, size, quantity, crusher model, drawing reference..." className="input resize-y" data-testid="field-message" />
                </Field>
              </div>
            </div>

            {status.state === "success" && (
              <div className="mt-6 flex items-start gap-3 bg-green-50 border border-green-200 p-4" data-testid="enquiry-success">
                <CheckCircle2 className="text-green-600 mt-0.5" size={20} />
                <div className="text-sm text-green-900">{status.message}</div>
              </div>
            )}
            {status.state === "error" && (
              <div className="mt-6 flex items-start gap-3 bg-red-50 border border-red-200 p-4" data-testid="enquiry-error">
                <AlertCircle className="text-red-600 mt-0.5" size={20} />
                <div className="text-sm text-red-900">{status.message}</div>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-xs text-[#94A3B8] uppercase tracking-[0.18em]">
                Or call <a href={COMPANY.phoneHref} className="text-[#0A192F] font-bold">{COMPANY.phone}</a>
              </div>
              <button
                type="submit"
                disabled={status.state === "loading"}
                className="bg-[#0EA5E9] hover:bg-[#0284C7] disabled:opacity-60 text-white px-7 py-4 font-bold text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2"
                data-testid="enquiry-submit"
              >
                {status.state === "loading" ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : "Send Enquiry"}
              </button>
            </div>
          </form>
        </div>
      </section>

      <style>{`
        .input { width: 100%; border: 1px solid #E2E8F0; background: #fff; padding: 12px 14px; font-size: 15px; color: #0A192F; outline: none; transition: border-color 150ms; }
        .input:focus { border-color: #0EA5E9; }
        .input::placeholder { color: #94A3B8; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#475569] mb-2">
        {label}{required && <span className="text-[#0EA5E9]"> *</span>}
      </span>
      {children}
    </label>
  );
}
